'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import SalesCallbackProvider from '@/components/sales-callback/SalesCallbackProvider';
import SalesCallbackSection from '@/components/sales-callback/SalesCallbackSection';
import CostCalculatorSkeleton from '../components/CostCalculatorSkeleton';
import type { LeadForm } from '@/types/lead-forms';

const CostCalculator = dynamic(() => import('../components/CostCalculator'), {
  ssr: false,
  loading: () => <CostCalculatorSkeleton />,
});

type IdleWindow = Window &
  typeof globalThis & {
    requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };

interface CostCalculatorClientProps {
  locale: string;
  calculatorData?: any;
  leadForms?: LeadForm[];
}

export default function CostCalculatorClient({
  locale,
  calculatorData,
  leadForms,
}: CostCalculatorClientProps) {
  const [shouldLoadCalculator, setShouldLoadCalculator] = useState(false);

  useEffect(() => {
    const idleWindow = window as IdleWindow;
    const load = () => setShouldLoadCalculator(true);
    const interactionEvents: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart'];

    interactionEvents.forEach((eventName) =>
      window.addEventListener(eventName, load, { passive: true, once: true }),
    );

    let idleId: number;
    let idleUsesCallback = false;
    if (idleWindow.requestIdleCallback) {
      idleUsesCallback = true;
      idleId = idleWindow.requestIdleCallback(load, { timeout: 1500 });
    } else {
      idleId = window.setTimeout(load, 750);
    }

    return () => {
      interactionEvents.forEach((eventName) => window.removeEventListener(eventName, load));
      if (idleUsesCallback) {
        idleWindow.cancelIdleCallback?.(idleId);
      } else {
        window.clearTimeout(idleId);
      }
    };
  }, []);

  return (
    <SalesCallbackProvider variant="calculator">
      <SalesCallbackSection mode="page" dwellMs={0}>
        {shouldLoadCalculator ? (
          <CostCalculator locale={locale} calculatorData={calculatorData} leadForms={leadForms} />
        ) : (
          <CostCalculatorSkeleton />
        )}
      </SalesCallbackSection>
    </SalesCallbackProvider>
  );
}
